import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useAppSelector } from '../hooks/useAppSelector';
import { useAppDispatch } from '../hooks/useAppDispatch';
import { hideNotification } from '../store/slices/notificationPopupSlice';
import { setOrderIdToOpen } from '../store/slices/orderModalSlice';
import restroIcon from '../assets/restro_ai_faviicon.png';
import { playNotificationSound } from '../utils/notificationSound';

const NotificationPopUp = () => {
  const { show, title, message, orderId } = useAppSelector((state) => state.notificationPopup);
  const dispatch = useAppDispatch();
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (!show) return;

    // Play sound when notification appears (shared util works immediately)
    playNotificationSound();

    timeoutRef.current = setTimeout(() => {
      dispatch(hideNotification());
    }, 5000);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [show, dispatch]);

  const handleClick = () => {
    if (orderId) {
      dispatch(setOrderIdToOpen(orderId));
    }
    dispatch(hideNotification());
  };

  if (!show) return null;

  const content = (
    <>
      <div
        role="alert"
        className="fixed top-0 left-0 right-0 z-[99999] px-3 pt-2 pb-3"
        style={{ paddingTop: 'max(0.5rem, env(safe-area-inset-top, 0.5rem))' }}
      >
        <div
          className="max-w-md mx-auto bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden animate-slide-down flex items-start gap-3 p-4"
          onClick={handleClick}
        >
          <img
            src={restroIcon}
            alt="RestroAI"
            className="w-12 h-12 flex-shrink-0 rounded-xl object-contain bg-gray-100"
          />
          <div className="flex-1 min-w-0">
            <p className="font-bold text-gray-900 text-sm leading-tight">{title}</p>
            <p className="text-gray-600 text-sm mt-0.5 line-clamp-2">{message}</p>
            {orderId && (
              <p className="text-blue-600 text-xs font-medium mt-1">Tap to view order details</p>
            )}
          </div>
          <button
            type="button"
            aria-label="Dismiss"
            className="flex-shrink-0 p-1 rounded-lg hover:bg-gray-100 text-gray-500"
            onClick={(e) => {
              e.stopPropagation();
              dispatch(hideNotification());
            }}
          >
            ✕
          </button>
        </div>
      </div>
      <style>{`
        @keyframes slide-down {
          from { transform: translateY(-100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .animate-slide-down {
          animation: slide-down 0.3s ease-out;
        }
      `}</style>
    </>
  );

  return createPortal(content, document.body);
};

export default NotificationPopUp;
